import {html, nothing, type TemplateResult} from 'lit';
import type {GoalCard} from '../quiz-controller';

/**
 * 今日の目標に届いたときの区切り。正解の自動送りの代わりに、選択肢と同じスロットへ
 * 1 セッションに 1 回だけ出す（category-suggestion.ts と同じ置き方）。
 *
 * 到達した瞬間は 1.3 秒の「目標達成」チップだけで、そのあとは数字が伸び続けるだけだった。
 * 学習を「勝って終える」場所を作り、次に何が起きるか（今日覚えた字が定着になる）を
 * その場で言う。感嘆符・絵文字は使わない。
 *
 * 主ボタンは「続ける」（塗り = 前へ進む、の既存規約）。「今日はここまで」は枠。
 * 止まってよいという合図は見出しと本文で足りていて、アプリが退出を勧めて見えないように
 */
export function goalCard(card: GoalCard, onClose: () => void, onContinue: () => void): TemplateResult {
  return html`
    <!-- 名前は見出し、説明は数字と次の一歩。○ の読み上げ「今日の目標に届きました」の
         1.3 秒後に同じ名前だけを読んでも情報が増えないので、describedby で本文を続ける -->
    <div
      id="dq-goal-card"
      role="group"
      aria-labelledby="dq-goal-card-title"
      aria-describedby="dq-goal-card-facts dq-goal-card-next"
      tabindex="-1"
      class="mt-8 rounded-xl bg-teal-50 p-5 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700"
    >
      <p id="dq-goal-card-title" class="text-lg font-semibold text-teal-800">
        今日の目標 ${card.goal} 問に届きました
      </p>
      <p id="dq-goal-card-facts" class="mt-1 text-sm text-gray-700 tabular-nums">
        今日 ${card.today} 問 ・ 正解 ${card.correct} 問
      </p>
      <p id="dq-goal-card-next" class="mt-2 text-sm text-gray-700">${nextStepLine(card.almost)}</p>
      <div class="mt-4 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          @click=${onContinue}
          class="px-6 py-3 bg-teal-700 hover:bg-teal-800 text-white font-medium rounded-lg shadow transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
        >
          続ける
        </button>
        <button
          type="button"
          @click=${onClose}
          class="px-6 py-3 bg-white border-2 border-teal-600 hover:bg-teal-100 text-teal-800 font-medium rounded-lg shadow-sm transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
        >
          今日はここまで
        </button>
      </div>
    </div>
  `;
}

/**
 * 次に何が起きるか。定着の条件は「別の暦日にもう一度正解する」なので、
 * 「明日開けば定着」とは言わない（パネルと読み上げの文言と同じ語で揃える）
 */
export function nextStepLine(almost: number): string {
  return almost > 0
    ? `今日覚えた字が ${almost} 字あります。日をあけてもう一度正解すると定着です`
    : 'また日をあけて練習すると、覚えた字が定着になります';
}

/**
 * 「今日はここまで」のあと、選択肢の代わりに置く休止のブロック。
 * 要素側がここへフォーカスを移す（押したボタンが消えるため）。まとめの本文は出題ボックスの
 * 中（restingText、id="dq-rest-summary"）にあるので、describedby で 1 回だけ読ませる
 */
export function sessionRest(onContinue: () => void): TemplateResult {
  return html`
    <div
      id="dq-session-rest"
      role="group"
      aria-label="今日はここまで"
      aria-describedby="dq-rest-summary"
      tabindex="-1"
      class="mt-8 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 rounded-xl"
    >
      <button
        type="button"
        @click=${onContinue}
        class="px-6 py-3 bg-teal-700 hover:bg-teal-800 text-white font-medium rounded-lg shadow transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
      >
        続ける
      </button>
    </div>
  `;
}

/**
 * 休止中に出題ボックスへ出すまとめの文。数字が無くても見出しだけは出す
 * （休止中に「文字の種類を選んでください」の空状態へ化けさせない）
 */
export function restingText(summary: GoalCard | null): TemplateResult {
  return html`
    <p class="text-lg font-semibold text-teal-800">今日はここまで</p>
    ${summary
      ? html`<p class="mt-1 text-sm text-gray-700 tabular-nums">
            今日 ${summary.today} 問 ・ 正解 ${summary.correct} 問
          </p>
          <p class="mt-2 text-sm text-gray-700">${nextStepLine(summary.almost)}</p>`
      : nothing}
  `;
}
